/**
 * PRP-ROOF-7 W3 (D9): the "Roof" card on the imported/chat estimate page.
 *
 * Not attached: a file input for the vendor roof file (EagleView XML or a wireframe roof ESX).
 * Attached: vendor, source, file, date, the roof variables, the four roofing lines as calcs,
 * any export blockers as a plain warning, and Remove. A refused upload shows the server's
 * reason inline; a failed request shows one line, never a blank card (degrade, never hard-fail).
 */
import { useEffect, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';

import {
  attachRoof,
  getRoof,
  isRefused,
  removeRoof,
  SOURCE_KIND_LABEL,
  type RoofView,
} from '../../../lib/roofAttachApi';
import { Button } from '../ui/button';
import { Card } from '../ui/card';

const HELP =
  'Attach the roof measurement file (EagleView XML, or a wireframe roof ESX). The export then carries the roof sketch and its roofing lines.';
const FAILED = 'Could not reach the server. The roof is unchanged.';

interface Props {
  parseHash: string;
}

const fmt = (n: number | undefined, digits = 0) =>
  typeof n === 'number' && Number.isFinite(n)
    ? n.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })
    : '—';

const when = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-US', { dateStyle: 'medium' });
};

function Fact({ k, v }: { k: string; v: string }) {
  return (
    <div className="min-w-0">
      <div className="text-[10.5px] text-[var(--tx-lo)] mb-0.5">{k}</div>
      <div className="text-[var(--tx-hi)] truncate" title={v}>
        {v}
      </div>
    </div>
  );
}

export default function RoofAttachCard({ parseHash }: Props) {
  const [roof, setRoof] = useState<RoofView | null>(null);
  const [blockers, setBlockers] = useState<string[]>([]);
  const [load, setLoad] = useState<'loading' | 'ready' | 'failed'>('loading');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ kind: 'ok' | 'bad'; msg: string } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    setLoad('loading');
    setNotice(null);
    getRoof(parseHash)
      .then((r) => {
        if (cancelled) return;
        setRoof(r.attached ? r.roof : null);
        setBlockers(r.export_blockers ?? []);
        setLoad('ready');
      })
      .catch(() => {
        if (!cancelled) setLoad('failed');
      });
    return () => {
      cancelled = true;
    };
  }, [parseHash]);

  async function onPick(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setNotice(null);
    try {
      const out = await attachRoof(parseHash, file);
      if (isRefused(out)) {
        setNotice({ kind: 'bad', msg: out.reason });
      } else {
        setRoof(out.roof);
        setBlockers(out.export_blockers ?? []);
        setNotice({ kind: 'ok', msg: 'Roof attached. The next export carries it.' });
      }
    } catch (err: unknown) {
      const status = (err as { status?: number } | null)?.status;
      setNotice({
        kind: 'bad',
        msg:
          status === 409
            ? 'The estimate changed while you were editing. Reload and try again.'
            : status === 413
              ? 'That file is too large (25 MB limit).'
              : FAILED,
      });
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  async function onRemove() {
    setBusy(true);
    setNotice(null);
    try {
      await removeRoof(parseHash);
      setRoof(null);
      setBlockers([]);
      setNotice({ kind: 'ok', msg: 'Roof removed. The file stays in your account; attach it again any time.' });
    } catch {
      setNotice({ kind: 'bad', msg: FAILED });
    } finally {
      setBusy(false);
    }
  }

  const heading = (
    <div className="mb-2 flex items-center gap-2">
      <h2 className="m-0 text-[13px] font-[560] uppercase tracking-wide text-[var(--tx-lo)]">Roof</h2>
      {roof && (
        <span className="text-[11px] text-[var(--tx-faint)]">
          {roof.vendor} · {SOURCE_KIND_LABEL[roof.source_kind] ?? roof.source_kind}
        </span>
      )}
    </div>
  );

  let body: JSX.Element;
  if (load === 'loading') {
    body = <div className="text-[var(--tx-lo)]">…</div>;
  } else if (load === 'failed') {
    body = <div className="text-[var(--tx-lo)]">Roof details are not available right now.</div>;
  } else if (!roof) {
    body = (
      <div className="flex flex-col gap-2">
        <p className="m-0 text-[12px] text-[var(--tx-lo)]">{HELP}</p>
        <label className="flex items-center gap-2 text-[12px] text-[var(--tx-hi)]">
          <span className="sr-only">Roof measurement file</span>
          <input
            ref={inputRef}
            type="file"
            accept=".xml,.esx"
            aria-label="Roof measurement file"
            data-testid="roof-file-input"
            disabled={busy}
            onChange={onPick}
            className="text-[12px]"
          />
          {busy && <span className="text-[var(--tx-faint)]">Reading…</span>}
        </label>
      </div>
    );
  } else {
    body = (
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2 text-[12.5px]">
          <Fact k="File" v={roof.filename_label} />
          <Fact k="Attached" v={when(roof.attached_at)} />
          <Fact k="Faces" v={fmt(roof.faces)} />
          <Fact k="Status" v={roof.status === 'ok' ? 'ready to export' : roof.status.replace(/_/g, ' ')} />
          <Fact k="SF" v={fmt(roof.sf)} />
          <Fact k="SQ" v={fmt(roof.sq, 2)} />
          <Fact k="P (perimeter LF)" v={fmt(roof.p)} />
          <Fact k="R / HIP / VAL (LF)" v={`${fmt(roof.r)} / ${fmt(roof.hip)} / ${fmt(roof.val)}`} />
        </div>
        <table className="w-full text-[12px] border-collapse" aria-label="Roofing lines">
          <thead>
            <tr className="text-left text-[10.5px] text-[var(--tx-lo)]">
              <th className="py-1 font-[560]">Code</th>
              <th className="py-1 font-[560]">Calc</th>
              <th className="py-1 font-[560] text-right">Qty</th>
              <th className="py-1 font-[560] pl-2">Unit</th>
            </tr>
          </thead>
          <tbody>
            {roof.lines.map((l) => (
              <tr key={l.code} data-testid="roof-line" className="border-t border-[var(--bd-subtle)]">
                <td className="py-1 font-[var(--font-mono)] text-[var(--tx-hi)]">{l.code}</td>
                <td className="py-1 font-[var(--font-mono)] text-[var(--tx-hi)]">{l.calc}</td>
                <td className="py-1 text-right text-[var(--tx-hi)]">{fmt(l.qty, 2)}</td>
                <td className="py-1 pl-2 text-[var(--tx-lo)]">{l.unit}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {blockers.length > 0 && (
          <p role="status" data-testid="roof-export-blockers" className="m-0 text-[11.5px] text-[var(--warn)]">
            Not in the export yet: {blockers.join('; ')}
          </p>
        )}
        <div>
          <Button size="sm" variant="ghost" type="button" onClick={onRemove} disabled={busy} aria-label="Remove roof">
            {busy ? 'Removing…' : 'Remove'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Card data-testid="roof-attach-card" className="px-[18px] py-4 mb-[14px]">
      {heading}
      {body}
      {notice && (
        <p
          role="status"
          data-testid="roof-attach-notice"
          className={`m-0 mt-2 text-[11.5px] ${notice.kind === 'ok' ? 'text-[var(--ok)]' : 'text-[var(--bad)]'}`}
        >
          {notice.msg}
        </p>
      )}
    </Card>
  );
}
